// src/utils/gunClient.ts
/// <reference types="vite/client" />
import Gun from "gun";
import "gun/sea";
// Removed gun/axe to prevent mesh flooding

// Custom Stable IndexedDB Adapter (Bypasses buggy RAD/Radix)
import "./gunIDBAdapter";
import { closeDB, withLocalStoreSuspended } from "./gunIDBAdapter";

import { looksLikePub } from './support/pubValidation';
import { closeIdbCache } from './support/idbCache';
import { localStore as storage } from './support/localStore';
import { GUN_STORAGE_KEYS as STORAGE_KEYS, GUN_STORAGE_PREFIX } from './gunConfig';

import { DEFAULT_PEERS, GUN_DB_NAME, GUN_ROOT } from './gunConfig';

// ------------------------------
// Types
// ------------------------------
type GunInstance = any;
type GunUser = any;

export type GunAuthSnapshot = {
  peers: string[];
  lastPeerStatus: { ok: boolean; at: number; note?: string } | null;
  lastError: { msg: string; at: number } | null;
  is: { pub?: string; epub?: string; alias?: string } | null;
  lastAlias: string | null;
  connectionState: ConnectionState;
};

// Connection states - SIMPLIFIED to binary only
type ConnectionState = 'connected' | 'disconnected';

// Enhanced connection status for detailed reporting
export interface ConnectionStatus {
  state: ConnectionState;
  isConnected: boolean;
  lastPeerStatus: { ok: boolean; at: number; note?: string } | null;
  lastError: { msg: string; at: number } | null;
  reconnectAttempts: number;
  peers: string[];
  // Additional diagnostic info
  timeSinceLastHi: number;
  connectionAge: number;
  // Peer connection details
  connectedPeers: number;
  totalPeers: number;
  // Raw peer info for advanced debugging
  peerDetails: Array<{
    url: string;
    connected: boolean;
    readyState?: number;
  }>;
}

// ------------------------------
// Config
// ------------------------------

/**
 * Relay peers, from `VITE_GUN_PEERS` (comma-separated) when set.
 *
 * Kept deliberately short: a strict list avoids retry-loops against dead peers.
 * Reading it from the environment is what lets this whole `data/gun` layer be
 * lifted into another project without editing source — the app's identity lives
 * in config, not in a hardcoded literal.
 */
export function getPeers(): string[] {
  const configured = import.meta.env.VITE_GUN_PEERS;
  if (typeof configured === "string" && configured.trim()) {
    const peers = configured
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    if (peers.length) return peers;
  }
  return DEFAULT_PEERS;
}

function now() {
  return Date.now();
}

/**
 * Verbose logging for the connection/auth flow.
 *
 * DEV ONLY: these lines carry the session pub, the alias and auth-flow state,
 * and they ran in production builds — anything with console access got a
 * readable transcript of the account. Opt in per build with
 * `VITE_GUN_DEBUG=1` when a deployed environment genuinely needs it.
 */
const DEBUG_GUN =
  String(import.meta.env?.VITE_GUN_DEBUG ?? '') === '1';

function log(...args: any[]) {
  if (DEBUG_GUN) {
    console.log("[gun]", ...args);
  }
}

function warn(...args: any[]) {
  if (DEBUG_GUN) {
    console.warn("[gun]", ...args);
  }
}

// ------------------------------
// Reconnection - SIMPLIFIED
// ------------------------------

// Simple reconnection: just re-add peers and optionally force a small put
// Gun's own automatic mesh + reconnect handles the rest
//
// Rate-limited, because "bye" is not a rare event: a flapping relay emits one
// per drop, and re-opting the peer list plus writing a heartbeat on every one
// turned a bad connection into a write amplifier. Gun's mesh already retries on
// its own schedule; this is only a nudge.
const RECONNECT_MIN_INTERVAL_MS = 10_000;
let lastReconnectAt = 0;

/**
 * Is any peer's WebSocket actually open right now?
 *
 * `null` when Gun's internals cannot be read (version drift, or before the
 * mesh has built `opt.peers`) — which is NOT the same as "no". Callers fall
 * back to the last `hi`/`bye` in that case.
 *
 * This is the honest answer to "are we connected". The `hi`/`bye` events are
 * only ever a NOTIFICATION that the answer changed, and Gun does not reliably
 * emit one on reconnect — so a client that had gone offline and come back
 * showed `disconnected` indefinitely while its socket was open and syncing.
 */
function socketIsOpen(gun: GunInstance): boolean | null {
  try {
    const peers = gun?.back?.('opt.peers');
    if (!peers || typeof peers !== 'object') return null;
    const list = Object.values(peers) as any[];
    if (list.length === 0) return null;
    let sawWire = false;
    for (const peer of list) {
      const wire = peer?.wire;
      if (!wire || typeof wire.readyState !== 'number') continue;
      sawWire = true;
      if (wire.readyState === 1) return true; // WebSocket.OPEN
    }
    return sawWire ? false : null;
  } catch {
    return null;
  }
}

/**
 * The connection state to report: the socket when it can be read, the last
 * event otherwise. Also CORRECTS the stored state, so a reconnect Gun never
 * announced stops being invisible to everything that reads `isPeerConnected`.
 */
function resolveConnectionState(st: GunState): ConnectionState {
  const open = socketIsOpen(st.gun);
  if (open === null) return st.connectionState;
  const observed: ConnectionState = open ? 'connected' : 'disconnected';
  if (observed !== st.connectionState) {
    st.connectionState = observed;
    st.lastPeerStatus = { ok: open, at: now(), note: open ? 'socket open' : 'socket closed' };
    log(`connection state corrected from the socket: ${observed}`);
  }
  return observed;
}

function attemptReconnect(st: GunState) {
  const nowMs = now();
  if (nowMs - lastReconnectAt < RECONNECT_MIN_INTERVAL_MS) return;
  lastReconnectAt = nowMs;
  try {
    // Re-add peers to existing Gun instance using opt()
    // This triggers GUN to re-establish connections
    (st.gun as any).opt({ peers: st.peers });
    
    // Optional: Force a small put to establish connection (as per GUN documentation)
    // This helps with receiving missing on() events.
    // Scoped per device: the old unscoped `__heartbeat/timestamp` soul was
    // shared by every client worldwide and churned constantly.
    setTimeout(() => {
      try {
        // Under GUN_ROOT like every other record this app writes. At the graph
        // ROOT it was outside the app's namespace entirely, so two apps sharing
        // a relay churned the same souls against each other.
        const deviceId = storage.get<string>(STORAGE_KEYS.DEVICE_ID, "") || "anon";
        // Raw `.put` rather than gunPut: gunWrite imports THIS module, so
        // importing it back would close a cycle for one write of a number that
        // needs no shape check. The ack handler is the part that mattered — a
        // reconnect nudge the relay rejects should not look like one that
        // worked.
        // eslint-disable-next-line no-restricted-syntax -- gunPut would close an import cycle; ack handled inline
        st.gun
          .get(GUN_ROOT)
          .get('__heartbeat')
          .get(String(deviceId))
          .put(Date.now(), (ack: any) => {
            if (ack?.err) warn('heartbeat rejected:', ack.err);
          });
      } catch (e) {
        // Ignore heartbeat errors
      }
    }, 100);
  } catch (e) {
    warn("Failed to opt peers for reconnection:", e);
  }
}

/**
 * How often to re-check the socket while it looks down.
 *
 * A watchdog used to live here and was removed on the reasoning that "Gun
 * handles connection health". Gun's own documentation says the opposite: after
 * going offline and back on it does not reliably reconnect, and peers can even
 * drop out of `opt.peers` — the fix being to re-`opt` them, which is what
 * {@link attemptReconnect} does.
 *
 * Without this, that nudge only ever ran on a `bye`. One `bye`, one attempt,
 * then nothing forever: a client that missed the event, or whose first attempt
 * failed, stayed offline until the user reloaded the page. `attemptReconnect`
 * is already rate-limited, so this cannot amplify into a write storm, and it
 * does nothing at all while the socket is open.
 */
const RECONNECT_WATCHDOG_MS = 15_000;

function startReconnectWatchdog(st: GunState) {
  if (typeof setInterval === 'undefined') return;
  const timer = setInterval(() => {
    const current = getGlobalState();
    // A hard reset replaces the state; the old instance's watchdog must not
    // keep nudging peers on an instance nobody is using any more.
    if (current !== st) {
      clearInterval(timer);
      return;
    }
    if (resolveConnectionState(st) === 'connected') return;
    warn('still disconnected — re-adding peers');
    attemptReconnect(st);
  }, RECONNECT_WATCHDOG_MS);
  // Never hold a Node process open for this (tests, SSR).
  (timer as any)?.unref?.();
  st.watchdog = timer;
}

// ------------------------------
// Singleton (HMR-safe)
// ------------------------------
type GunState = {
  gun: GunInstance;
  user: GunUser;
  peers: string[];
  lastPeerStatus: { ok: boolean; at: number; note?: string };
  lastError: { msg: string; at: number } | null;
  connectionState: ConnectionState;
  /** Resolvers waiting for the first "hi" from a peer. */
  hiWaiters: Array<() => void>;
  /** Periodic reconnect check; cleared when this state is retired. */
  watchdog?: ReturnType<typeof setInterval>;
};

function getGlobalState(): GunState | null {
  return (globalThis as any).__GUN_LAYER_STATE__ ?? null;
}

function setGlobalState(st: GunState) {
  (globalThis as any).__GUN_LAYER_STATE__ = st;
}

function createGunState(): GunState {
  const peers = getPeers();
  if (!peers.length) {
    warn("Gun peers missing. Set peers to your relay URL, e.g. https://yourdomain.com/gun");
  } else {
    log("Peers:", peers);
  }

  // IMPORTANT:
  // - Keep ONE Gun instance for the whole app.
  // - The custom IndexedDB adapter (./gunIDBAdapter) is the ONLY graph store.
  //   Gun's built-in localStorage store would mirror the entire graph into the
  //   ~5MB localStorage quota on top of IDB, so it stays off. SEA session
  //   persistence does not depend on it — we persist the pair ourselves
  //   (see gunAuth) and re-auth on boot.
  const gun: GunInstance = (Gun as any)({
    peers,
    localStorage: false,
    radisk: false,       // Bypasses the internal RADish/Radix engine
    // Turned off explicitly rather than by not importing `gun/axe`: AXE hunts
    // for peers on its own (LAN multicast, "last used peers", hard-coded
    // defaults), so a build that pulls it in transitively would silently join
    // a mesh this app never configured. The test suite was doing exactly that
    // — real instances with `peers: []` still dialled out.
    axe: false,
    multicast: false,
    // Opt-in flag for the IndexedDB adapter (see gunIDBAdapter): its plugin
    // registers on Gun's GLOBAL 'opt' event, so without this gate every Gun
    // instance in the process — including test-local ones — silently shared
    // the same IDB store.
    gunLayerIDB: true,
  } as any);

  const user = gun.user();

  const st: GunState = {
    gun,
    user,
    peers,
    lastPeerStatus: { ok: false, at: now(), note: "init" },
    lastError: null,
    connectionState: 'disconnected',
    hiWaiters: [],
  };

  try {
    // ONLY trust Gun events for connection state.
    // NOTE: this is the ONE AND ONLY root "hi" listener. Gun's root chain has
    // no per-handler off(), so registering one per waitForPeer() call either
    // leaked listeners or (via gun.off("hi", h)) detached these connection-state
    // handlers entirely, freezing connectionState.
    gun.on("hi", () => {
      st.lastPeerStatus = { ok: true, at: now(), note: "hi" };
      st.connectionState = 'connected';
      log("peer hi - connected");
      const waiters = st.hiWaiters;
      st.hiWaiters = [];
      for (const w of waiters) {
        try { w(); } catch { /* ignore */ }
      }
    });

    gun.on("bye", () => {
      st.lastPeerStatus = { ok: false, at: now(), note: "bye" };
      st.connectionState = 'disconnected';
      warn("peer bye - disconnected");
      attemptReconnect(st);
    });

    startReconnectWatchdog(st);

    // REMOVED: error event handler - let Gun handle its own errors
  } catch {
    // ignore
  }

  return st;
}

/**
 * Returns the single Gun instance for the whole app.
 * HMR-safe: stored in globalThis.
 */
export function gunInstance(): GunInstance {
  const existing = getGlobalState();
  if (existing?.gun) return existing.gun;

  const st = createGunState();

  /**
   * Surface every rejected write, whoever made it.
   *
   * This replaces a "traffic monitor" whose entire body was commented out, so
   * it ran `Object.keys(msg.put)` on every inbound message to reach a no-op.
   *
   * It earns the hot-path slot now: most writes in this app are bare
   * `node.put(x)` with no ack callback, and a relay rejection for one of those
   * had nowhere to go — an unsigned put, an oversized value or an unverified
   * write simply looked like it had worked. Gun routes every ack through `in`,
   * so one listener here covers writes that predate {@link gunPut} and any that
   * slip past it. Errors only: acks themselves are far too frequent to log.
   */
  (st.gun as any).on('in', (msg: any) => {
    if (msg?.err) console.error('[gun] relay rejected a write:', msg.err, '(ack for', msg['@'], ')');
  });

  setGlobalState(st);
  return st.gun;
}

/**
 * Returns the single Gun user handle for the whole app.
 */
export function gunUser(): GunUser {
  const st = getGlobalState();
  if (st?.user) return st.user;
  gunInstance();
  return (getGlobalState() as GunState).user;
}

/**
 * The current session's SEA pair, or null when logged out.
 *
 * THE accessor for the pair. Reaching into `user()._?.sea` is a Gun-internals
 * idiom that was copy-pasted across the codebase; it belongs in exactly one
 * place so a Gun upgrade that moves it breaks one line.
 */
export function sessionPair(): any | null {
  try {
    const u = gunUser();
    return (u as any)?.is && (u as any)?._?.sea ? (u as any)._.sea : null;
  } catch {
    return null;
  }
}

/** The current session's pub, or null when logged out. */
export function sessionPub(): string | null {
  try {
    return (gunUser() as any)?.is?.pub ?? null;
  } catch {
    return null;
  }
}

/**
 * Root node for the app.
 *
 * This namespace is PUBLIC and UNSIGNED: any peer that knows the relay URL can
 * write anywhere under it. Use it only for records that are inherently shared
 * (chat, connection records, the directory, the verification queue) and that
 * carry their own signature, and treat everything read from it as untrusted.
 * Records a single user owns belong under {@link ownedRoot}.
 */
export function publicRoot(): any {
  const node = gunInstance().get(GUN_ROOT);
  return node;
}

/**
 * Root node for records ONE user owns — projects, time entries, timers,
 * settings, profile.
 *
 * Gun enforces write authority only inside the SEA user graph, so these live
 * under `~<pub>` where nobody but the key holder can write them. Reading
 * someone else's data still works (a client viewing a freelancer's projects);
 * it just resolves to the same soul through the read-only path.
 *
 * Guests have no keypair, so they keep using the public namespace. They have no
 * account to protect and can only collide with themselves there.
 */
export function ownedRoot(pub: string): any {
  const id = String(pub || "").trim();
  if (!looksLikePub(id)) return publicRoot().get(id);

  // ALWAYS use the public read-only node path for reads/subscriptions so that Gun DB
  // correctly queries and syncs data from the relay peers.
  const node = gunInstance().get(`~${id}`);
  // Namespaced so app records can't collide with Gun's own user-graph keys
  // (pub, epub, alias, auth).
  return node.get(GUN_ROOT);
}

/**
 * Thrown when a write is attempted against a soul this client cannot sign for.
 */
export class GunWriteAuthorityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GunWriteAuthorityError';
  }
}

/**
 * Assert that this client may WRITE to `ownedRoot(pub)`, and return the node.
 *
 * `ownedRoot` resolves the same soul whether or not a SEA session is live, so its
 * result looks identical in both cases — but only the `gunUser()` branch signs.
 * Without a session the chain is `gun.get('~<pub>')`, and a put there goes out
 * unsigned: the relay drops it, while the local IndexedDB adapter still
 * acknowledges it, so the write reported success and simply never existed
 * anywhere else. The app used to avoid this by plumbing a `sessionReady` flag
 * from the auth context down through every provider — a guard the next project
 * would have to reinvent. It belongs here.
 *
 * Guest ids (device ids, not pubs) resolve to the public namespace, which needs
 * no key, so they always pass.
 */
export function ownedRootWritable(pub: string): any {
  const id = String(pub || "").trim();
  if (!looksLikePub(id)) return publicRoot().get(id);

  const mine = getGlobalState()?.user?.is?.pub;
  if (!mine) {
    throw new GunWriteAuthorityError(
      `Refusing to write to ~${id.slice(0, 8)}…: no SEA session, so the put would be unsigned.`,
    );
  }
  if (String(mine) !== id) {
    throw new GunWriteAuthorityError(
      `Refusing to write to ~${id.slice(0, 8)}…: signed in as ~${String(mine).slice(0, 8)}…. ` +
        'Only the key holder can write their own graph.',
    );
  }
  return gunUser().get(GUN_ROOT);
}

/**
 * The current session's own owned root. Throws when no SEA session is live —
 * see {@link ownedRootWritable}.
 */
export function myRoot(): any {
  const mine = getGlobalState()?.user?.is?.pub;
  if (!mine) {
    throw new GunWriteAuthorityError('No SEA session: there is no "my" graph to write.');
  }
  return gunUser().get(GUN_ROOT);
}

/**
 * Explicit read-only accessor for another party's owned records. Same soul as
 * {@link ownedRoot}; the separate name is the documentation — a write through this
 * chain cannot be signed and will be dropped by the relay.
 */
export function readOnlyRoot(pub: string): any {
  return ownedRoot(pub);
}

/**
 * Is a relay currently connected?
 *
 * Cheap, synchronous, and does NOT create the Gun singleton — callers use it to
 * decide how much a write ack is worth (see gunWrite), and creating the client
 * as a side effect of that question would be surprising.
 */
export function isPeerConnected(): boolean {
  const st = getGlobalState();
  if (!st) return false;
  // Reads the socket, not the last event — see resolveConnectionState. A write
  // helper deciding whether a local-only ack is good enough must not be told
  // "offline" by a `bye` that a reconnect already made stale.
  return resolveConnectionState(st) === 'connected';
}

/**
 * This account's device list.
 *
 * Under `timeTracker` like every other app record. It used to sit at the SEA
 * user root next to Gun's own account keys (pub, epub, alias, auth) — the exact
 * collision {@link ownedRoot}'s namespacing exists to prevent.
 */
export function devicesNode(pub: string): any {
  return ownedRoot(pub).get("devices");
}

// ------------------------------
// Peer readiness gate - SIMPLIFIED
// ------------------------------
export function waitForPeer(timeoutMs = 5000): Promise<void> {
  gunInstance(); // ensure the singleton (and its single "hi" listener) exists
  const st = getGlobalState();

  if (!st) return Promise.reject(new Error("gun not initialised"));
  if (st.connectionState === 'connected') {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    let settled = false;

    const t = setTimeout(() => {
      if (settled) return;
      settled = true;
      // Drop ourselves from the shared waiter list; no root listener to detach.
      // gunHardReset migrates pending waiters into the FRESH state's list, so
      // remove from the current state too — filtering only the captured one
      // leaked the closure into the new instance's array on every reset.
      st.hiWaiters = st.hiWaiters.filter((w) => w !== waiter);
      const current = getGlobalState();
      if (current && current !== st) {
        current.hiWaiters = current.hiWaiters.filter((w) => w !== waiter);
      }
      reject(new Error("peer timeout"));
    }, timeoutMs);

    const waiter = () => {
      if (settled) return;
      settled = true;
      clearTimeout(t);
      resolve();
    };

    st.hiWaiters.push(waiter);
  });
}

// ------------------------------
// WebSocket connectivity test
// ------------------------------
/**
 * Ask Gun to reconnect now, regardless of what it last announced.
 *
 * Exposed so a diagnostic that has just PROVED the relay reachable can act on
 * that instead of merely reporting it: the WebSocket test opens its own socket,
 * so a passing test alongside a `disconnected` client is exactly the state the
 * re-`opt` fixes, and the user was left to reload the page themselves.
 */
export function reconnectPeers(): void {
  const st = getGlobalState();
  if (st) attemptReconnect(st);
}

export function updatePeers(newPeers: string[]): void {
  const st = getGlobalState();
  if (!st) return;
  st.peers = [...newPeers];
  try {
    st.gun.opt({ peers: newPeers });
    log("Peers updated at runtime:", newPeers);
  } catch (e) {
    warn("Failed to update peers:", e);
  }
}

export async function testWebSocket(peerUrl: string): Promise<{ ok: boolean; reason?: string }> {
  return new Promise((resolve) => {
    // Convert http:// to ws:// and https:// to wss://
    const wsUrl = peerUrl.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
    const ws = new WebSocket(wsUrl);
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      ws.close();
      resolve({ ok: false, reason: 'timeout' });
    }, 5000);

    ws.onopen = () => {
      clearTimeout(timeout);
      ws.close();
      // The relay is reachable. If Gun is not on it, this is the moment to
      // re-add the peers — a test that can fix what it just measured beats one
      // that tells the user to reload.
      reconnectPeers();
      resolve({ ok: true });
    };

    ws.onerror = () => {
      clearTimeout(timeout);
      if (!timedOut) resolve({ ok: false, reason: 'network_error' });
    };

    ws.onclose = (e: CloseEvent) => {
      clearTimeout(timeout);
      if (!timedOut) {
        resolve({ ok: false, reason: `code=${e.code} ${e.reason || ''}`.trim() });
      }
    };
  });
}

// ------------------------------
// Auth helpers
// ------------------------------
export function setLastAlias(alias: string) {
  try {
    const a = String(alias || "").trim();
    if (!a) return;

    // ✅ never persist pub-like strings as alias
    if (looksLikePub(a)) return;

    storage.set(STORAGE_KEYS.LAST_ALIAS, a);
  } catch { }
}

export function clearLastAlias() {
  try {
    storage.remove(STORAGE_KEYS.LAST_ALIAS);
  } catch { }
}

export function getLastAlias(): string | null {
  try {
    const v = storage.get<string>(STORAGE_KEYS.LAST_ALIAS, "");
    return v ? String(v) : null;
  } catch {
    return null;
  }
}

// Implementation lives in @shared/utils/pubValidation (pure, Gun-free);
// re-exported here so existing data-layer callers keep working.
export { looksLikePub };

// REMOVED: gunCreate(). Nothing called it, and it timed out u.create without
// clearing SEA's in-flight latch — leaving every later auth refused with
// "User is already being created or authenticated!" until something else
// happened to clear it. Account creation goes through gunRegisterAndLogin in
// gunAuth, which owns the latch machinery.

// REMOVED: gunLoginLowLevel(). It returned success for ANY alias/password
// whenever a session already existed, without ever verifying the password —
// and it was exported through ./index. Use gunLogin() from gunAuth instead,
// which always performs a real u.auth().

/**
 * Callbacks that drop per-account state held outside Gun.
 *
 * Every module that memoizes something derived from the session — a pairwise
 * SEA secret, a replay high-water mark, a sequence allocator — has to forget it
 * when the account changes, or the previous account's value is silently applied
 * to the next one.
 *
 * This is deliberately NOT "on logout". A logout is only one of the two ways an
 * account stops being current: the app can also authenticate a different one
 * over a live session (the sync modal does exactly that), and `gunLogin` clears
 * the old session with `user.leave()` rather than by calling {@link gunLogout}.
 * Five modules registered a cleanup here believing it covered both; none of them
 * ran on a switch, so account A's pairwise secrets decrypted account B's call
 * signalling and A's chain marks were persisted under B's key.
 *
 * Both paths now call {@link runSessionReset}, so a hook fires whenever the
 * authenticated pub stops being the one the state belongs to.
 */
const sessionResetHooks: Array<() => void> = [];

/**
 * Register a callback invoked whenever the current account stops being current
 * — logout OR a login as a different pub. Returns an unregister function —
 * modules re-registering across HMR reloads must call it, or hooks accumulate.
 */
export function onSessionReset(fn: () => void): () => void {
  sessionResetHooks.push(fn);
  return () => {
    const i = sessionResetHooks.indexOf(fn);
    if (i >= 0) sessionResetHooks.splice(i, 1);
  };
}

/**
 * App-registered extra cleanup for {@link resetLocalAuthAndCache}.
 *
 * The layer cannot know which app-level caches exist (it used to delete the
 * time tracker's entries/projects caches by name — app knowledge that does not
 * travel when this folder is lifted into another project). Register whatever
 * the app must wipe alongside the Gun graph here.
 */
const localWipeHooks: Array<() => void | Promise<void>> = [];
export function onLocalDataWipe(fn: () => void | Promise<void>): () => void {
  localWipeHooks.push(fn);
  return () => {
    const i = localWipeHooks.indexOf(fn);
    if (i >= 0) localWipeHooks.splice(i, 1);
  };
}

/**
 * Run every {@link onSessionReset} hook. Called by {@link gunLogout} and by
 * `gunLogin` when it authenticates a pub other than the one already in session.
 */
export function runSessionReset() {
  for (const hook of sessionResetHooks) {
    try { hook(); } catch { /* a bad hook must not block the reset */ }
  }
}

/**
 * Logout current user.
 * Note: SEA session may still persist in localStorage unless you clear it explicitly.
 */
export function gunLogout(): { ok: boolean; error?: string } {
  const u = gunUser();
  try {
    u.leave();
    // `leave()` does not touch SEA's in-flight latch. If a login was abandoned
    // on a timeout the latch is still set, and logging out then in again would
    // be refused with "User is already being created or authenticated!".
    try {
      if ((u as any)?._?.ing) (u as any)._.ing = false;
    } catch { /* best effort */ }
    runSessionReset();
    storage.remove(STORAGE_KEYS.GUN_PAIR);
    storage.remove(STORAGE_KEYS.ACTIVE_USER);
    clearLastAlias();
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: e?.message || String(e) };
  }
}

/**
 * Close every websocket the instance holds open.
 *
 * `gun.off()` does NOT do this — it clears `cat.any` (the chain's listeners)
 * and nothing else. So a hard reset built on `off()` alone left the previous
 * instance's peers connected and reconnecting on Gun's own schedule, while a
 * fresh instance opened its own: the wedged sockets a reset exists to clear
 * survived it, and each reset added another set.
 *
 * Reaches into `opt.peers`, which is internal and version-dependent — hence
 * best-effort per peer rather than one try/catch around the lot.
 */
function closePeerSockets(gun: GunInstance) {
  try {
    const peers = (gun as any)?.back?.('opt.peers');
    if (!peers || typeof peers !== 'object') return;
    for (const peer of Object.values(peers) as any[]) {
      try {
        // `retry = 0` first: Gun's mesh reopens a peer whose retry budget is
        // left intact, so closing the wire alone just triggers a reconnect.
        if (peer) peer.retry = 0;
        peer?.wire?.close?.();
      } catch {
        // One unclosable peer must not leave the rest open.
      }
    }
  } catch {
    // Internals differ across Gun versions; the reset still proceeds.
  }
}

/**
 * Hard reset Gun client (sockets wedged, etc.).
 */
export function gunHardReset() {
  const st = getGlobalState();
  if (!st) return;

  try {
    st.user?.leave?.();
    // A hard reset exists to unstick a wedged client; the auth latch is one of
    // the things that can be wedged.
    if ((st.user as any)?._?.ing) (st.user as any)._.ing = false;
  } catch { }

  try {
    st.gun?.off?.();
  } catch { }
  // Listeners are only half of it — see closePeerSockets.
  closePeerSockets(st.gun);

  (globalThis as any).__GUN_LAYER_STATE__ = null;
  gunInstance();

  // Callers parked in waitForPeer were waiting on the OLD instance's "hi",
  // which will never come. Hand them to the new instance so they resolve when
  // it connects — otherwise every in-flight wait across a reset was a
  // guaranteed timeout even if the new instance connected instantly.
  const pending = st.hiWaiters.splice(0);
  const fresh = getGlobalState();
  if (fresh && pending.length > 0) {
    if (fresh.connectionState === 'connected') {
      for (const w of pending) {
        try { w(); } catch { /* a waiter must not block the rest */ }
      }
    } else {
      fresh.hiWaiters.push(...pending);
    }
  }
}

/**
 * Tear the Gun client down WITHOUT respawning it — the counterpart to
 * {@link gunHardReset}, for tests and for hosts with their own lifecycle
 * (an embedding app unmounting the feature, a test file cleaning up).
 *
 * Sockets are closed, listeners detached, pending waitForPeer callers rejected
 * (their state is gone; letting them hang would be the bug this layer keeps
 * fixing elsewhere), and the global slot cleared. The next gunInstance() call starts
 * a fresh instance.
 */
export function destroyGunClient(): void {
  const st = getGlobalState();
  if (!st) return;
  try {
    st.user?.leave?.();
    if ((st.user as any)?._?.ing) (st.user as any)._.ing = false;
  } catch { /* best effort */ }
  // Stop nudging peers on an instance that is being thrown away. The watchdog
  // also self-cancels when the global state changes, but a destroyed client
  // sets that to null, so clear it here rather than wait a tick.
  if (st.watchdog) clearInterval(st.watchdog);
  try {
    st.gun?.off?.();
  } catch { /* best effort */ }
  closePeerSockets(st.gun);
  // Parked waitForPeer callers are dropped, not resolved: their contract is
  // "called = connected", which a destroyed client cannot honestly claim.
  // Each carries its own timeout and rejects on schedule.
  st.hiWaiters.splice(0);
  (globalThis as any).__GUN_LAYER_STATE__ = null;
}

/**
 * Robust session recall: poll briefly for u.is.pub.
 * Returns pub or null.
 */
export async function gunRecallSession(timeoutMs = 1500, pollMs = 80): Promise<string | null> {
  log('[gunRecallSession] Starting session recall. timeoutMs:', timeoutMs, 'pollMs:', pollMs);
  const u = gunUser();
  const started = Date.now();
  
  log('[gunRecallSession] Initial u.is?.pub:', u?.is?.pub);

  let iterations = 0;
  while (Date.now() - started < timeoutMs) {
    iterations++;
    const pub = u?.is?.pub ? String(u.is.pub) : null;
    if (pub) {
      log('[gunRecallSession] session found internally after', Date.now() - started, 'ms (iterations:', iterations, '). pub:', pub);
      return pub;
    }
    await new Promise((r) => setTimeout(r, pollMs));
  }
  
  log('[gunRecallSession] session NOT found after', timeoutMs, 'ms (iterations:', iterations, '). u.is at end:', u?.is);
  return null;
}

// ------------------------------
// Cleanup helpers
// ------------------------------
const IDB_DELETE_MAX_ATTEMPTS = 10;
const IDB_DELETE_RETRY_MS = 300;

/**
 * Delete one IndexedDB database, resolving true ONLY when the delete request
 * actually fired `onsuccess`.
 *
 * A blocked request means another connection still holds the database open —
 * resolving on `onblocked` (as this used to) left the previous account's cached
 * graph, including profile PII, alive across an account switch. So we close our
 * own connection, then retry a bounded number of times and report failure
 * honestly if the database survives.
 */
async function deleteIndexedDBWithRetry(dbName: string): Promise<boolean> {
  for (let attempt = 1; attempt <= IDB_DELETE_MAX_ATTEMPTS; attempt++) {
    // Always drop our own handle first, otherwise we block ourselves.
    try {
      closeDB();
    } catch {
      // best-effort
    }

    const outcome = await new Promise<'success' | 'blocked' | 'error'>((resolve) => {
      let settled = false;
      const settle = (r: 'success' | 'blocked' | 'error') => {
        if (settled) return;
        settled = true;
        resolve(r);
      };
      try {
        const req = indexedDB.deleteDatabase(dbName);
        req.onsuccess = () => settle('success');
        req.onerror = () => settle('error');
        // Do NOT settle here: a blocked request can still succeed once the
        // other connection closes. Wait for the real onsuccess; the retry
        // timer below is what bounds the wait.
        req.onblocked = () => {
          console.warn(`[clearIndexedDB] Delete of ${dbName} blocked (attempt ${attempt})`);
        };
        setTimeout(() => settle('blocked'), IDB_DELETE_RETRY_MS);
      } catch {
        settle('error');
      }
    });

    if (outcome === 'success') return true;
    if (outcome === 'error') return false;
    // blocked → loop and retry
  }
  return false;
}

/**
 * Best-effort by name, but NOT by outcome: a database that could not be
 * deleted is logged loudly, because the leftover graph belongs to the previous
 * account. Returns true only when every target database was really deleted.
 */
export async function clearIndexedDBBestEffort(namePrefix?: string): Promise<boolean> {
  // optional cleanup; safe best-effort
  if (typeof indexedDB === 'undefined') return false;

  // If a specific prefix is provided, only delete matching databases
  // Otherwise, delete all known app databases
  const targetDbs = namePrefix ? [namePrefix] : [GUN_DB_NAME];

  let allDeleted = true;
  for (const dbName of targetDbs) {
    let deleted = false;
    try {
      deleted = await deleteIndexedDBWithRetry(dbName);
    } catch (e) {
      console.warn(`[clearIndexedDB] Failed to delete ${dbName}:`, e);
      deleted = false;
    }
    if (!deleted) {
      allDeleted = false;
      console.error(
        `[clearIndexedDB] Could NOT delete ${dbName}; the previous session's cached graph may still be on this device.`,
      );
    }
  }
  return allDeleted;
}

/**
 * Wipe the locally cached Gun graph. Used on account switch so the previous
 * user's cached data — including profile PII — does not survive into the next
 * session.
 * Does NOT touch the device id or tear down the live Gun instance.
 *
 * The Gun instance keeps running throughout, so the local store is SUSPENDED
 * for the duration: otherwise the adapter reopens the database the delete just
 * closed (blocking the delete, which retries, closing it again) and the
 * incoming account's nodes are cached into a store that is about to be
 * destroyed. Await this before letting the new session start syncing.
 */
export async function clearGunLocalGraph(): Promise<boolean> {
  return await withLocalStoreSuspended(() => clearIndexedDBBestEffort());
}

/**
 * Every IndexedDB database this origin has, not just the ones this layer named.
 *
 * `indexedDB.databases()` is Chromium/WebKit-and-modern-Firefox only. Where it
 * is missing the known names are the fallback — which is exactly the case the
 * old reset always took, so it is no worse than before, just no longer the
 * ceiling.
 */
async function allOriginDatabases(): Promise<string[]> {
  const known = [GUN_DB_NAME, `${GUN_STORAGE_PREFIX}_data_cache`];
  try {
    const listed = await (indexedDB as any).databases?.();
    if (Array.isArray(listed)) {
      const names = listed.map((d: any) => d?.name).filter((n: any): n is string => !!n);
      return [...new Set([...names, ...known])];
    }
  } catch {
    /* not supported here — fall through to the known names */
  }
  return known;
}

/**
 * Delete EVERYTHING this origin stores. Not a cache flush — a factory reset.
 *
 * Scope is deliberately the whole origin rather than this layer's own keys:
 *
 * - **localStorage and sessionStorage, entirely.** The old reset swept a list
 *   of prefixes, so any key that predated the naming convention survived a
 *   "clear all local data" the user had explicitly asked for. Three did.
 *   A prefix list is a thing to forget to update; `clear()` is not.
 * - **Every IndexedDB database**, enumerated at runtime. Deleting only the
 *   CURRENT graph name left every earlier one behind — and a graph name is
 *   bumped precisely when its contents must go, so the one database guaranteed
 *   to be missed was the one that mattered.
 * - **Cache Storage, and every service worker registration.** A PWA that
 *   deletes its data and keeps serving the bundle that wrote it will repopulate
 *   from a stale worker on the next load, and the user cannot tell that from a
 *   reset that silently failed.
 *
 * The device id goes too. It used to be preserved so the at-rest key for the
 * stored SEA pair stayed derivable — but that pair is one of the things being
 * destroyed, so preserving its key only left an identifier behind.
 *
 * localStorage is per-origin, so "everything" here means everything this app
 * ever wrote and nothing belonging to anyone else. A host app with its own
 * storage on the same origin should not call this.
 *
 * Reload afterwards. In-memory state still references what was deleted, and
 * the caller is expected to be a confirmed, destructive user action.
 */
export async function resetLocalAuthAndCache(): Promise<void> {
  // 1) End the session first, so nothing writes back into what we are deleting.
  gunLogout();

  // 2) App-registered cleanup (see onLocalDataWipe) — anything the app holds
  //    outside these three stores, which the layer cannot know about.
  for (const hook of localWipeHooks) {
    try {
      await hook();
    } catch { /* a bad hook must not block the reset */ }
  }

  // 3) Web storage, in full.
  try { localStorage.clear(); } catch { /* unavailable */ }
  try { sessionStorage.clear(); } catch { /* unavailable */ }

  // 4) Release every connection this app holds BEFORE deleting. The graph
  //    store is closed by withLocalStoreSuspended below; the data cache holds
  //    its own connection for the life of the page and had nothing closing it,
  //    so its delete always blocked — and a blocked delete stays pending and
  //    stalls later opens, which stopped the local store acking writes and left
  //    Gun's auth waiting for an ack that never came.
  closeIdbCache();
  await withLocalStoreSuspended(async () => {
    for (const name of await allOriginDatabases()) {
      try {
        if (!(await deleteIndexedDBWithRetry(name))) {
          console.error(`[reset] could NOT delete database ${name}; data may remain on this device.`);
        }
      } catch (e) {
        console.error(`[reset] failed deleting database ${name}:`, e);
      }
    }
    return true;
  });

  // 5) Cache Storage + service workers, so the next load is genuinely fresh.
  try {
    const keys = await caches?.keys?.();
    if (keys) await Promise.all(keys.map((k) => caches.delete(k)));
  } catch { /* Cache API unavailable */ }
  try {
    const regs = await navigator.serviceWorker?.getRegistrations?.();
    if (regs) await Promise.all(regs.map((r) => r.unregister()));
  } catch { /* no service worker */ }

  // 6) Drop the live Gun instance last: everything above wanted it quiet, not
  //    gone, and tearing it down first would let it rebuild state mid-wipe.
  gunHardReset();
}

// ------------------------------
// Debug snapshot
// ------------------------------
export function gunDebugSnapshot(): GunAuthSnapshot {
  const st = getGlobalState();
  const u = st?.user;
  // Correct from the socket first: this snapshot feeds the panel's "Status"
  // line, which is the readout that sat on `disconnected` while the socket was
  // open. `lastPeerStatus` is read AFTER, so the note reflects the correction.
  const connectionState = st ? resolveConnectionState(st) : 'disconnected';
  return {
    peers: st?.peers ?? [],
    lastPeerStatus: st?.lastPeerStatus ?? null,
    lastError: st?.lastError ?? null,
    is: u?.is ? { pub: u.is.pub, alias: u.is.alias, epub: u.is.epub } : null,
    lastAlias: getLastAlias(),
    connectionState,
  };
}

/**
 * Get detailed connection status for debugging and monitoring
 * Provides enhanced information beyond the basic snapshot
 */
export function getConnectionStatus(): ConnectionStatus {
  const st = getGlobalState();
  if (!st) {
    return {
      state: 'disconnected',
      isConnected: false,
      lastPeerStatus: null,
      lastError: null,
      reconnectAttempts: 0,
      peers: [],
      timeSinceLastHi: 0,
      connectionAge: 0,
      connectedPeers: 0,
      totalPeers: 0,
      peerDetails: [],
    };
  }

  // Correct the stored state from the socket before reporting anything, so
  // the panel and `isPeerConnected` never disagree.
  const state = resolveConnectionState(st);

  const nowTime = now();
  const timeSinceLastHi = st.lastPeerStatus?.at 
    ? nowTime - st.lastPeerStatus.at 
    : 0;
    
  const connectionAge = st.lastPeerStatus?.at 
    ? nowTime - st.lastPeerStatus.at 
    : 0;

  // Calculate connected peers by checking Gun's peer connections
  // Following Gun documentation: filter peers with active WebSocket connections
  let connectedPeers = 0;
  const totalPeers = st.peers.length;
  
  // Create peer details for debugging with actual connection states
  const peerDetails = st.peers.map(url => {
    // Try to get peer info from Gun's opt.peers if available
    let peerInfo = null;
    let isConnected = false;
    let readyState = 3; // Default to CLOSED
    
    try {
      // Access Gun's internal peer tracking (this may vary by Gun version)
      const gunAny = st.gun as any;
      if (gunAny?.back?.('opt.peers')) {
        const optPeers = gunAny.back('opt.peers');
        const peersArray = Object.values(optPeers);
        peerInfo = peersArray.find((p: any) => p && p._?.url === url);
        
        if (peerInfo) {
          // Safely access wire property
          const wire = (peerInfo as any).wire;
          if (wire && typeof wire === 'object') {
            // Check if wire has the expected properties
            const readyStateVal = (wire as any).readyState;
            const openVal = (wire as any).OPEN;
            isConnected = (typeof readyStateVal === 'number' && readyStateVal === 1 && 
                          typeof openVal === 'number' && openVal === 1);
            readyState = typeof readyStateVal === 'number' ? readyStateVal : 3;
            if (isConnected) connectedPeers++;
          }
        }
      }
    } catch (e) {
      // Fallback to connection state if we can't access internal peer info
      isConnected = st.connectionState === 'connected';
      readyState = st.connectionState === 'connected' ? 1 : 3;
      if (isConnected) connectedPeers = totalPeers;
    }
    
    // Base peer detail object
    const baseDetail: any = {
      url,
      connected: isConnected,
      readyState
    };
    
    // Include additional debug info if available and safe to access
    try {
      if (peerInfo) {
        const wire = (peerInfo as any).wire;
        if (wire && typeof wire === 'object') {
          baseDetail.constructorName = (wire as any).constructor?.name || undefined;
          baseDetail.wireUrl = (wire as any).url || undefined;
        }
      }
    } catch (e) {
      // Ignore errors in debug info extraction
    }
    
    return baseDetail;
  });

  return {
    state,
    isConnected: state === 'connected',
    lastPeerStatus: st.lastPeerStatus,
    lastError: st.lastError,
    reconnectAttempts: 0, // No longer tracking - Gun handles it
    peers: st.peers,
    timeSinceLastHi,
    connectionAge,
    connectedPeers,
    totalPeers,
    peerDetails,
  };
}

if (typeof window !== 'undefined') {
  (window as any).gunDebug = gunDebugSnapshot;
  (window as any).gunConnectionStatus = getConnectionStatus;
}

